export type SignatureDimensions = {
  width: number;
  height: number;
};

export type SignaturePosition = {
  x: number;
  y: number;
};

export enum SignatureStatus {
  Signed = 'SIGNED',
  UnSigned = 'UNSIGNED',
}

export enum SignatureType {
  Signature = 'SIGNATURE',
}

export enum AssignedSigneeType {
  User = 'USER',
  Person = 'PERSON',
}

interface BaseSignatureBoxApiResponse {
  signature_box_id: string;
  properties: SignatureBoxProperties;
  type: SignatureType;
}

interface SignatureEventApiResponse {
  id: string;
  email: string;
  last_name: string;
  first_name: string;
  initials: string;
  ip_address: string;
  signed_date: string;
}

interface SignedApiResponse {
  status: SignatureStatus.Signed;
  signature_event: SignatureEventApiResponse;
}

interface UnsignedApiResponse {
  status: SignatureStatus.UnSigned;
  signature_event: null;
}

export interface AssignedSigneeApiResponse {
  first_name: string;
  last_name: string;
  displayable_name?: string;
  email: string;
  type: AssignedSigneeType;
  id: number;
}

interface AssignedApiResponse {
  assigned_signee: AssignedSigneeApiResponse;
}

interface UnAssignedApiResponse {
  assigned_signee: null;
}

interface UnAssignedSignedSignatureBoxApiResponse extends BaseSignatureBoxApiResponse, UnAssignedApiResponse, SignedApiResponse {}
interface UnAssignedUnSignedSignatureBoxApiResponse extends BaseSignatureBoxApiResponse, UnAssignedApiResponse, UnsignedApiResponse {}

interface AssignedSignedSignatureBoxApiResponse extends BaseSignatureBoxApiResponse, AssignedApiResponse, SignedApiResponse {}
interface AssignedUnSignedSignatureBoxApiResponse extends BaseSignatureBoxApiResponse, AssignedApiResponse, UnsignedApiResponse {}

type UnAssignedSignatureBoxApiResponse = UnAssignedSignedSignatureBoxApiResponse | UnAssignedUnSignedSignatureBoxApiResponse;
type AssignedSignatureBoxApiResponse = AssignedSignedSignatureBoxApiResponse | AssignedUnSignedSignatureBoxApiResponse;

export type SignatureBoxApiResponse = UnAssignedSignatureBoxApiResponse | AssignedSignatureBoxApiResponse;
export type SignedSignatureBoxApiResponse = UnAssignedSignedSignatureBoxApiResponse | AssignedSignedSignatureBoxApiResponse;

export interface GetSignaturesByDocumentIdApiResponse {
  sectionsSignatures: { [sectionId: string]: SignatureBoxApiResponse[] };
}

export interface SignatureBoxProperties {
  dimensions: SignatureDimensions;
  position: SignaturePosition;
}

export interface BaseSignatureBox {
  signatureBoxId: string;
  type: SignatureType;
  properties: SignatureBoxProperties;
}

export interface SignatureEvent {
  id: string;
  email: string;
  lastName: string;
  firstName: string;
  initials: string;
  ipAddress: string;
  signedDate: string;
}

export interface Signed {
  status: SignatureStatus.Signed;
  signatureEvent: SignatureEvent;
}

export interface UnSigned {
  status: SignatureStatus.UnSigned;
  signatureEvent: null;
}

interface UnAssigned {
  assignedSignee: AssignedSignee | null;
}

export interface AssignedSignee {
  email: string;
  firstName: string;
  lastName: string;
  displayableName?: string;
  type: AssignedSigneeType;
  id: number | string;
  // Required by the WS assign schema alongside `signeeType`. Optional here because
  // signature boxes read back from the API don't include it, only newly picked signees do.
  uid?: string;
  signeeType?: AssignedSigneeType;
}

export interface Assigned {
  assignedSignee: AssignedSignee;
}

export interface UnAssignedSignedSignatureBox extends BaseSignatureBox, UnAssigned, Signed {}
export interface UnAssignedUnSignedSignatureBox extends BaseSignatureBox, UnAssigned, UnSigned {}

export interface AssignedSignedSignatureBox extends BaseSignatureBox, Assigned, Signed {}
export interface AssignedUnSignedSignatureBox extends BaseSignatureBox, Assigned, UnSigned {}

type UnAssignedSignatureBox = UnAssignedSignedSignatureBox | UnAssignedUnSignedSignatureBox;
type AssignedSignatureBox = AssignedSignedSignatureBox | AssignedUnSignedSignatureBox;
export type UnSignedSignatureBox = UnAssignedUnSignedSignatureBox | AssignedUnSignedSignatureBox;
export type SignedSignatureBox = UnAssignedSignedSignatureBox | AssignedSignedSignatureBox;
export type SignatureBox = UnAssignedSignatureBox | AssignedSignatureBox;

export type SectionsSignaturesType = { [sectionId: string]: SignatureBox[] };

export type SanitizedAssignedSignee = Pick<AssignedSignee, 'id' | 'type'> &
  Partial<Pick<AssignedSignee, 'firstName' | 'lastName' | 'email' | 'displayableName'>>;

// Names can be '' or missing (e.g. display-name-only CRM contacts); never render "undefined".
export function signeeName(
  { firstName, lastName, displayableName, email }: Pick<AssignedSignee, 'firstName' | 'lastName' | 'displayableName' | 'email'>,
  abbreviateLast = false
): string {
  const last = abbreviateLast && lastName ? `${lastName[0]}.` : lastName;
  const name = firstName || lastName ? `${firstName ?? ''} ${last ?? ''}`.trim() : displayableName;
  return name || email || '';
}

// SIGNATURE_REASSIGN_SIGNEE requires { id, type }; names are optional but persisted, so send them.
export function sanitizeAssignedSigneeForReassign(signee: AssignedSignee | null | undefined): SanitizedAssignedSignee | null {
  if (!signee) return null;
  const { id, type, firstName, lastName, email, displayableName } = signee;
  // CRM contacts can carry null names; the schema rejects null.
  return {
    id,
    type,
    ...(firstName != null && { firstName }),
    ...(lastName != null && { lastName }),
    ...(email != null && { email }),
    ...(displayableName != null && { displayableName }),
  };
}

// SIGNATURE_ADD/SIGNATURE_DELETE require the full signee record (email, firstName, lastName
// are required by the backend schema), but signee options sourced from SignatureSelectOption
// carry UI-only fields (label, value, initials) the schema rejects as additional properties.
// displayableName is dropped too: it's valid on the reassign schema but not on this one, and
// signees loaded from the API (ApiSignatureDocumentServiceRepository) do carry it.
// Strip down to exactly the AssignedSignee shape.
export function sanitizeAssignedSignee(signee: AssignedSignee | null | undefined): AssignedSignee | null {
  if (!signee) return null;
  const { email, firstName, lastName, type, id, uid, signeeType } = signee;
  return {
    email,
    firstName,
    lastName,
    type,
    id,
    uid,
    signeeType,
  };
}
